import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { apiClient, formatApiError, API } from "@/lib/auth";
import { useLabel, useLayout, Section } from "@/lib/dynamicEngine";
import NexWordmark from "@/components/NexWordmark";
import { KindIcon, KindBadge, fmtBytes } from "@/pages/admin/documents/kindMeta";
import {
  ArrowLeft, Mail, Phone, Building2, Loader2, Send, Copy, Link2, Tag as TagIcon,
  Sparkles, Video, ExternalLink, CheckCircle2, Trash2, Plus, X, Upload, FileText,
  Pencil, Check,
} from "lucide-react";
import { toast } from "sonner";

const KIND_ICONS = {
  brief_submitted:     "📝",
  contact_created:     "✨",
  contact_updated:     "✏️",
  stage_changed:       "🔀",
  tags_updated:        "🏷",
  note_added:          "🗒",
  email_sent:          "✉️",
  sms_sent:            "💬",
  link_created:        "🔗",
  link_opened:         "👁",
  link_step_completed: "✅",
  checkout_started:    "💳",
  payment_received:    "💰",
  consultation_booked: "📅",
};

const ContactDetailPage = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState("");
  const [tagInput, setTagInput] = useState("");
  // Dynamic Engine — labels + layout
  const notesHeading = useLabel("contact_detail.notes.heading", "// ADD NOTE");
  const timelineHeading = useLabel("contact_detail.timeline.heading", "// UNIFIED TIMELINE");
  const filesHeading = useLabel("contact_detail.files.heading", "// FILES");
  const linksHeading = useLabel("contact_detail.links.heading", "// MICRO-LANDING PAGES");
  const tagsHeading = useLabel("contact_detail.tags.heading", "// TAGS");
  const contactHeading = useLabel("contact_detail.contact.heading", "// CONTACT");
  useLayout("contact_detail", [
    { id: "contact_card",   order: 0 },
    { id: "tags_block",     order: 1 },
    { id: "note_block",     order: 2 },
    { id: "action_links",   order: 3 },
    { id: "files_block",    order: 4 },
    { id: "timeline_block", order: 5 },
  ]);
  // Action-link form
  const [showLink, setShowLink] = useState(false);
  const [lTitle, setLTitle] = useState("Your Exchange Alliance Onboarding");
  const [lSubtitle, setLSubtitle] = useState("Complete these steps to activate your engine");
  const [lVideo, setLVideo] = useState("");
  const [lBody, setLBody] = useState("");
  const [lSteps, setLSteps] = useState([
    { title: "Sign NDA", kind: "link", href: "" },
    { title: "Upload Logo", kind: "form", prompt: "Paste your logo URL" },
    { title: "Pay Deployment Fee", kind: "checkout" },
  ]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await apiClient.get(`/crm/contacts/${id}`);
      setData(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Documents attached to this contact
  const [docs, setDocs] = useState([]);
  const [docBusy, setDocBusy] = useState(false);
  const fileRef = React.useRef(null);
  const loadDocs = React.useCallback(async () => {
    try {
      const { data } = await apiClient.get(`/documents?contact_id=${id}`);
      setDocs(data.items);
    } catch { /* ignore */ }
  }, [id]);
  useEffect(() => { loadDocs(); }, [loadDocs]);

  const uploadFile = async (file) => {
    if (!file) return;
    setDocBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("contact_id", id);
      await apiClient.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded ${file.name}`);
      if (fileRef.current) fileRef.current.value = "";
      await Promise.all([loadDocs(), load()]);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setDocBusy(false); }
  };

  const openDoc = (docId) => {
    const token = localStorage.getItem("nex_token");
    window.open(`${API}/documents/${docId}/download?auth=${encodeURIComponent(token)}`, "_blank", "noopener");
  };

  const removeDoc = async (docId) => {
    if (!window.confirm("Delete this file?")) return;
    try {
      await apiClient.delete(`/documents/${docId}`);
      await loadDocs();
      toast.success("Deleted");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const setStage = async (stage) => {
    try {
      await apiClient.post(`/crm/contacts/${id}/stage`, { stage });
      toast.success(`Moved to ${stage.replace(/_/g, " ")}`);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const addTag = async () => {
    const t = tagInput.trim();
    if (!t) return;
    const next = Array.from(new Set([...(data.contact.tags || []), t]));
    try {
      await apiClient.post(`/crm/contacts/${id}/tags`, { tags: next });
      setTagInput("");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const removeTag = async (t) => {
    const next = (data.contact.tags || []).filter(x => x !== t);
    try {
      await apiClient.post(`/crm/contacts/${id}/tags`, { tags: next });
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const saveNote = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    try {
      await apiClient.post(`/crm/contacts/${id}/notes`, { body: note.trim() });
      setNote("");
      await load();
      toast.success("Note added");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // ── Edit / Delete note (timeline event) ──
  const [editingId, setEditingId] = useState(null);
  const [editBody, setEditBody] = useState("");

  const startEditNote = (event) => {
    setEditingId(event.id);
    setEditBody(event.payload?.body || "");
  };
  const cancelEditNote = () => {
    setEditingId(null);
    setEditBody("");
  };
  const saveEditNote = async () => {
    const trimmed = editBody.trim();
    if (!trimmed) { toast.error("Note cannot be empty"); return; }
    try {
      await apiClient.patch(`/crm/contacts/${id}/notes/${editingId}`, { body: trimmed });
      cancelEditNote();
      await load();
      toast.success("Note updated");
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const deleteEvent = async (eventId, label) => {
    if (!window.confirm(`Delete this ${label.toLowerCase()}? It will be hidden from the timeline.`)) return;
    try {
      await apiClient.delete(`/crm/contacts/${id}/timeline/${eventId}`);
      await load();
      toast.success("Deleted");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // ── Edit contact info (name / email / business / phone / industry) ──
  const [editingContact, setEditingContact] = useState(false);
  const [contactDraft, setContactDraft] = useState({});
  const [savingContact, setSavingContact] = useState(false);

  const startEditContact = () => {
    setContactDraft({
      full_name:     data?.contact?.full_name || "",
      email:         data?.contact?.email || "",
      business_name: data?.contact?.business_name || "",
      phone:         data?.contact?.phone || "",
      industry:      data?.contact?.industry || "",
    });
    setEditingContact(true);
  };
  const cancelEditContact = () => {
    setEditingContact(false);
    setContactDraft({});
  };
  const saveEditContact = async () => {
    const name = (contactDraft.full_name || "").trim();
    const email = (contactDraft.email || "").trim();
    if (!name)  { toast.error("Name is required"); return; }
    if (!email) { toast.error("Email is required"); return; }
    setSavingContact(true);
    try {
      await apiClient.patch(`/crm/contacts/${id}`, {
        full_name:     name,
        email,
        business_name: (contactDraft.business_name || "").trim() || null,
        phone:         (contactDraft.phone || "").trim() || null,
        industry:      (contactDraft.industry || "").trim() || null,
      });
      cancelEditContact();
      await load();
      toast.success("Contact updated");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSavingContact(false); }
  };

  const createLink = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data: res } = await apiClient.post("/crm/action-links", {
        contact_id: id,
        title: lTitle,
        subtitle: lSubtitle,
        kind: "onboarding",
        video_url: lVideo || null,
        body_md: lBody,
        steps: lSteps.map(s => ({ ...s })),
      });
      toast.success(`Link created · theexchangealliance.org/c/${res.link.token}`);
      setShowLink(false);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/c/${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copied");
  };

  const createResponseLink = async () => {
    try {
      const { data } = await apiClient.post("/crm/response-links", { contact_id: id });
      navigator.clipboard.writeText(data.url);
      toast.success(`Response link copied — ${data.url}`);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteLink = async (linkId) => {
    if (!window.confirm("Delete this link?")) return;
    try {
      await apiClient.delete(`/crm/action-links/${linkId}`);
      await load();
      toast.success("Deleted");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center">
      <Loader2 className="animate-spin text-nex-gold" />
    </div>
  );
  if (!data) return null;
  const { contact, timeline, action_links, stages } = data;

  return (
    <div className="min-h-screen bg-[#0B0F19]">
      <div className="border-b border-nex-border bg-black/40 backdrop-blur">
        <div className="w-full mx-auto px-6 md:px-10 lg:px-16 xl:px-24 2xl:px-32 py-4 flex items-center gap-4">
          <Link to="/admin/contacts" className="flex items-center gap-2 text-nex-chrome hover:text-nex-gold">
            <ArrowLeft size={14} /> <span className="mono-body text-[11px] uppercase tracking-[0.2em]">Contacts</span>
          </Link>
          <span className="text-nex-muted">·</span>
          <NexWordmark size="text-lg" />
        </div>
      </div>

      <div className="w-full mx-auto px-6 md:px-10 lg:px-16 xl:px-24 2xl:px-32 py-8 grid lg:grid-cols-[360px_1fr] gap-6" data-testid="contact-detail">
        {/* LEFT — Contact card + tags + notes + stage */}
        <aside className="space-y-6">
          <Section scope="contact_detail" id="contact_card">
          <div className="card-industrial corner-tick p-6" data-testid="contact-card">
            <div className="flex items-center justify-between mb-3">
              <div className="mono-label">{contactHeading}</div>
              {!editingContact && (
                <button
                  onClick={startEditContact}
                  className="text-nex-muted hover:text-nex-gold p-1 -mr-1"
                  title="Edit contact info"
                  data-testid="contact-edit-btn"
                >
                  <Pencil size={13} />
                </button>
              )}
            </div>

            {editingContact ? (
              <div className="space-y-3" data-testid="contact-edit-form">
                <div>
                  <label className="mono-label !text-[9px] mb-1 block">FULL NAME</label>
                  <input
                    value={contactDraft.full_name}
                    onChange={(e) => setContactDraft({ ...contactDraft, full_name: e.target.value })}
                    className="input-industrial w-full"
                    autoFocus
                    data-testid="contact-edit-name"
                  />
                </div>
                <div>
                  <label className="mono-label !text-[9px] mb-1 block">EMAIL</label>
                  <input
                    type="email"
                    value={contactDraft.email}
                    onChange={(e) => setContactDraft({ ...contactDraft, email: e.target.value })}
                    className="input-industrial w-full"
                    data-testid="contact-edit-email"
                  />
                </div>
                <div>
                  <label className="mono-label !text-[9px] mb-1 block">BUSINESS</label>
                  <input
                    value={contactDraft.business_name}
                    onChange={(e) => setContactDraft({ ...contactDraft, business_name: e.target.value })}
                    className="input-industrial w-full"
                    placeholder="Company name"
                    data-testid="contact-edit-business"
                  />
                </div>
                <div>
                  <label className="mono-label !text-[9px] mb-1 block">PHONE</label>
                  <input
                    value={contactDraft.phone}
                    onChange={(e) => setContactDraft({ ...contactDraft, phone: e.target.value })}
                    className="input-industrial w-full"
                    placeholder="+1 555-000-0000"
                    data-testid="contact-edit-phone"
                  />
                </div>
                <div>
                  <label className="mono-label !text-[9px] mb-1 block">INDUSTRY</label>
                  <input
                    value={contactDraft.industry}
                    onChange={(e) => setContactDraft({ ...contactDraft, industry: e.target.value })}
                    className="input-industrial w-full"
                    placeholder="Trades · Retail · SaaS..."
                    data-testid="contact-edit-industry"
                  />
                </div>
                <div className="flex gap-2 pt-1">
                  <button
                    onClick={saveEditContact}
                    disabled={savingContact}
                    className="tick-btn-primary text-xs !py-2 !px-3 flex-1 justify-center"
                    data-testid="contact-edit-save"
                  >
                    {savingContact ? <Loader2 size={12} className="animate-spin" /> : <><Check size={12} /> Save</>}
                  </button>
                  <button
                    onClick={cancelEditContact}
                    disabled={savingContact}
                    className="tick-btn-secondary text-xs !py-2 !px-3"
                    data-testid="contact-edit-cancel"
                  >
                    <X size={12} /> Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <h1 className="serif-head text-2xl mb-1">{contact.full_name}</h1>
                {contact.business_name && (
                  <div className="text-nex-chrome text-sm flex items-center gap-2 mb-3">
                    <Building2 size={13} /> {contact.business_name}
                  </div>
                )}
                <div className="space-y-2 text-sm mb-4">
                  <a href={`mailto:${contact.email}`} className="text-nex-silver hover:text-nex-gold inline-flex items-center gap-2 break-all">
                    <Mail size={13} /> {contact.email}
                  </a>
                  {contact.phone && (
                    <div className="text-nex-chrome flex items-center gap-2">
                      <Phone size={13} /> {contact.phone}
                    </div>
                  )}
                  {contact.industry && (
                    <div className="text-nex-muted mono-body text-[11px] uppercase tracking-widest">
                      {contact.industry}
                    </div>
                  )}
                </div>
              </>
            )}

            <div className="border-t border-nex-border pt-4">
              <div className="mono-label !text-[9px] mb-2">STAGE</div>
              <select
                value={contact.stage}
                onChange={(e) => setStage(e.target.value)}
                className="input-industrial w-full"
                data-testid="stage-select"
              >
                {stages.map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
              </select>
            </div>
            <div className="border-t border-nex-border pt-4 mt-4">
              <div className="mono-label !text-[9px] mb-2">LEAD SCORE</div>
              <div className="serif-head text-2xl text-nex-gold">{contact.lead_score || 0}</div>
            </div>
          </div>
          </Section>

          <Section scope="contact_detail" id="tags_block">
          <div className="card-industrial corner-tick p-5" data-testid="tags-block">
            <div className="mono-label mb-3">{tagsHeading}</div>
            <div className="flex flex-wrap gap-2 mb-3">
              {(contact.tags || []).map(t => (
                <span key={t} className="mono-body text-[10px] uppercase text-nex-gold border border-nex-gold/30 px-2 py-1 inline-flex items-center gap-1">
                  <TagIcon size={9} /> {t}
                  <button onClick={() => removeTag(t)} className="ml-1 hover:text-red-300"><X size={11} /></button>
                </span>
              ))}
              {(contact.tags || []).length === 0 && <span className="text-nex-muted text-xs">No tags yet</span>}
            </div>
            <div className="flex gap-2">
              <input value={tagInput} onChange={e => setTagInput(e.target.value)} placeholder="Add tag" className="input-industrial flex-1" onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addTag())} data-testid="tag-input" />
              <button onClick={addTag} className="tick-btn-secondary text-xs !py-2 !px-3" data-testid="tag-add">Add</button>
            </div>
          </div>
          </Section>

          <Section scope="contact_detail" id="note_block">
          <form onSubmit={saveNote} className="card-industrial corner-tick p-5" data-testid="note-block">
            <div className="mono-label mb-3">{notesHeading}</div>
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={3} placeholder="What did they say on the call?" className="input-industrial w-full mb-3" data-testid="note-input" />
            <button type="submit" disabled={!note.trim()} className="tick-btn-primary w-full justify-center text-xs" data-testid="note-save">
              <Send size={13} /> Log note
            </button>
          </form>
          </Section>
        </aside>

        {/* RIGHT — Action Links + Timeline */}
        <main className="space-y-6 min-w-0">
          {/* ACTION LINKS */}
          <Section scope="contact_detail" id="action_links">
          <section className="card-industrial corner-tick p-6" data-testid="action-links-block">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Sparkles size={16} className="text-nex-gold" />
                <div className="mono-label">{linksHeading}</div>
              </div>
              <button onClick={() => setShowLink(s => !s)} className="tick-btn-primary text-xs !py-2 !px-3" data-testid="new-link-btn">
                <Plus size={13} /> New link
              </button>
              <button onClick={createResponseLink} className="tick-btn-secondary text-xs !py-2 !px-3" data-testid="new-response-link-btn" title="Copies a /r/ one-tap response link">
                <Sparkles size={13} /> Response link
              </button>
            </div>
            {showLink && (
              <form onSubmit={createLink} className="border border-nex-border p-4 mb-4 space-y-3" data-testid="new-link-form">
                <input value={lTitle} onChange={e => setLTitle(e.target.value)} placeholder="Title" className="input-industrial w-full" data-testid="link-title" />
                <input value={lSubtitle} onChange={e => setLSubtitle(e.target.value)} placeholder="Subtitle" className="input-industrial w-full" data-testid="link-subtitle" />
                <div className="flex gap-2 items-center">
                  <Video size={14} className="text-nex-muted" />
                  <input value={lVideo} onChange={e => setLVideo(e.target.value)} placeholder="Loom or YouTube embed URL (optional)" className="input-industrial flex-1" data-testid="link-video" />
                </div>
                <textarea value={lBody} onChange={e => setLBody(e.target.value)} rows={2} placeholder="Short greeting (optional, markdown)" className="input-industrial w-full" data-testid="link-body" />
                <div className="space-y-2">
                  <div className="mono-label !text-[9px]">Onboarding steps</div>
                  {lSteps.map((s, i) => (
                    <div key={i} className="grid grid-cols-[1fr_140px_1fr_auto] gap-2">
                      <input value={s.title} onChange={e => setLSteps(prev => prev.map((x, xi) => xi === i ? { ...x, title: e.target.value } : x))} placeholder="Step title" className="input-industrial" />
                      <select value={s.kind} onChange={e => setLSteps(prev => prev.map((x, xi) => xi === i ? { ...x, kind: e.target.value } : x))} className="input-industrial">
                        <option value="confirm">Confirm click</option>
                        <option value="link">External link</option>
                        <option value="form">Form input</option>
                        <option value="checkout">Stripe checkout</option>
                      </select>
                      <input
                        value={s.kind === "link" ? (s.href || "") : (s.prompt || "")}
                        onChange={e => setLSteps(prev => prev.map((x, xi) => xi === i ? { ...x, [s.kind === "link" ? "href" : "prompt"]: e.target.value } : x))}
                        placeholder={s.kind === "link" ? "URL" : s.kind === "form" ? "Prompt" : "—"}
                        disabled={s.kind === "confirm" || s.kind === "checkout"}
                        className="input-industrial disabled:opacity-40"
                      />
                      <button type="button" onClick={() => setLSteps(prev => prev.filter((_, xi) => xi !== i))} className="text-nex-muted hover:text-red-300"><Trash2 size={14} /></button>
                    </div>
                  ))}
                  <button type="button" onClick={() => setLSteps(prev => [...prev, { title: "New step", kind: "confirm" }])} className="mono-body text-[11px] uppercase text-nex-gold">+ add step</button>
                </div>
                <button type="submit" disabled={busy} className="tick-btn-primary w-full justify-center" data-testid="link-submit">
                  {busy ? <Loader2 size={13} className="animate-spin" /> : <>Create link <Sparkles size={13} /></>}
                </button>
              </form>
            )}

            {action_links.length === 0 ? (
              <div className="text-center py-6 text-nex-chrome text-sm">
                No links yet. Create one to send this contact a tokenized action page.
              </div>
            ) : (
              <div className="space-y-3">
                {action_links.map(l => (
                  <div key={l.id} className="border border-nex-border p-4" data-testid={`link-row-${l.id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-nex-silver">{l.title}</div>
                        <div className="mono-body text-[10px] text-nex-muted mt-1">
                          theexchangealliance.org/c/{l.token} · {l.click_count} view{l.click_count === 1 ? "" : "s"}
                          {l.last_seen_at && ` · last opened ${new Date(l.last_seen_at).toLocaleString()}`}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => copyLink(l.token)} className="tick-btn-secondary text-xs !py-1.5 !px-2" data-testid={`copy-link-${l.id}`}>
                          <Copy size={12} /> Copy
                        </button>
                        <a href={`/c/${l.token}`} target="_blank" rel="noopener noreferrer" className="tick-btn-secondary text-xs !py-1.5 !px-2" data-testid={`open-link-${l.id}`}>
                          <ExternalLink size={12} /> Open
                        </a>
                        <button onClick={() => deleteLink(l.id)} className="text-nex-muted hover:text-red-300 p-1" data-testid={`delete-link-${l.id}`}>
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                    <div className="mt-3 flex gap-1">
                      {l.steps.map((s, i) => (
                        <div key={i} className={`flex-1 h-1 ${s.completed_at ? "bg-emerald-500" : i === l.current_step_index ? "bg-nex-gold" : "bg-white/10"}`} title={s.title} />
                      ))}
                    </div>
                    <div className="mono-body text-[10px] text-nex-muted mt-2">
                      Step {Math.min(l.current_step_index + 1, l.steps.length)} of {l.steps.length}
                      {l.current_step_index >= l.steps.length && <span className="text-emerald-300"> · complete</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
          </Section>

          {/* FILES */}
          <Section scope="contact_detail" id="files_block">
          <section className="card-industrial corner-tick p-6" data-testid="files-block">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <FileText size={16} className="text-nex-gold" />
                <div className="mono-label">{filesHeading} ({docs.length})</div>
              </div>
              <button onClick={() => fileRef.current?.click()} disabled={docBusy} className="tick-btn-primary text-xs !py-2 !px-3" data-testid="contact-upload-btn">
                {docBusy ? <><Loader2 size={13} className="animate-spin" /> Uploading</> : <><Upload size={13} /> Upload</>}
              </button>
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.md"
                onChange={e => uploadFile(e.target.files?.[0])}
                className="hidden"
                data-testid="contact-file-input"
              />
            </div>
            {docs.length === 0 ? (
              <div className="text-center py-6 text-nex-chrome text-sm">
                No files yet. Upload PDFs, Word docs, Excel sheets, or CSVs.
              </div>
            ) : (
              <div className="space-y-2" data-testid="contact-files-list">
                {docs.map(d => (
                  <div key={d.id} className="flex items-center gap-3 border border-nex-border p-3" data-testid={`contact-doc-${d.id}`}>
                    <KindIcon kind={d.kind} size={16} />
                    <div className="flex-1 min-w-0">
                      <div className="text-nex-silver truncate text-sm">{d.original_filename}</div>
                      <div className="mono-body text-[10px] text-nex-muted mt-1">
                        {fmtBytes(d.size)} · {new Date(d.created_at).toLocaleDateString()} · {d.uploaded_by}
                      </div>
                    </div>
                    <KindBadge kind={d.kind} />
                    <button onClick={() => openDoc(d.id)} className="tick-btn-secondary text-xs !py-1.5 !px-2">
                      <ExternalLink size={12} />
                    </button>
                    <button onClick={() => removeDoc(d.id)} className="text-nex-muted hover:text-red-300 p-1">
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>
          </Section>

          {/* TIMELINE */}
          <Section scope="contact_detail" id="timeline_block">
          <section className="card-industrial corner-tick p-6" data-testid="timeline-block">
            <div className="mono-label mb-4">{timelineHeading} ({timeline.length})</div>
            {timeline.length === 0 ? (
              <div className="text-center py-6 text-nex-chrome text-sm">No events yet.</div>
            ) : (
              <div className="space-y-3">
                {timeline.map((e) => {
                  const isNote = e.kind === "note_added";
                  const isEditing = editingId === e.id;
                  return (
                    <div key={e.id} className="group flex gap-3 pb-3 border-b border-nex-border/40 last:border-b-0" data-testid={`event-${e.id}`}>
                      <div className="text-2xl leading-none">{KIND_ICONS[e.kind] || "•"}</div>
                      <div className="flex-1 min-w-0">
                        {isEditing ? (
                          <div className="space-y-2">
                            <textarea
                              value={editBody}
                              onChange={(ev) => setEditBody(ev.target.value)}
                              rows={3}
                              className="input-industrial w-full text-sm"
                              autoFocus
                              data-testid={`event-edit-input-${e.id}`}
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={saveEditNote}
                                className="tick-btn-primary text-xs !py-1.5 !px-3"
                                data-testid={`event-edit-save-${e.id}`}
                              >
                                <Check size={12} /> Save
                              </button>
                              <button
                                onClick={cancelEditNote}
                                className="tick-btn-secondary text-xs !py-1.5 !px-3"
                                data-testid={`event-edit-cancel-${e.id}`}
                              >
                                <X size={12} /> Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="text-nex-silver text-sm">
                              {e.label}
                              {e.payload?.body && <span className="text-nex-chrome"> — {e.payload.body}</span>}
                              {e.payload?.step_title && <span className="text-nex-chrome"> — {e.payload.step_title}</span>}
                              {e.payload?.from && e.payload?.to && (
                                <span className="text-nex-chrome"> — {e.payload.from} → {e.payload.to}</span>
                              )}
                            </div>
                            <div className="mono-body text-[10px] text-nex-muted mt-1">
                              {new Date(e.at).toLocaleString()}
                              {e.actor && ` · ${e.actor}`}
                              {e.edited_at && (
                                <span className="text-nex-gold/70"> · edited {new Date(e.edited_at).toLocaleString()}{e.edited_by ? ` by ${e.edited_by}` : ""}</span>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                      {!isEditing && (
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          {isNote && (
                            <button
                              onClick={() => startEditNote(e)}
                              className="text-nex-muted hover:text-nex-gold p-1"
                              title="Edit note"
                              data-testid={`event-edit-${e.id}`}
                            >
                              <Pencil size={13} />
                            </button>
                          )}
                          <button
                            onClick={() => deleteEvent(e.id, e.label || "entry")}
                            className="text-nex-muted hover:text-red-300 p-1"
                            title="Delete entry"
                            data-testid={`event-delete-${e.id}`}
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
          </Section>
        </main>
      </div>
    </div>
  );
};

export default ContactDetailPage;
